import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useCallback, useEffect, useState } from 'react';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useNavigate } from 'react-router-dom';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { Box } from '@mui/system';
import { getAllCustomer } from 'src/shared/services/customer';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { CustomerSettingsOverviewSearchFilter } from '../models/customerSettingsOverviewSearchFilter';
import { CustomerSettings } from '../models/customerSettings';

type CustomerSettingsDataGridProps = {
  searchFilter: CustomerSettingsOverviewSearchFilter;
  searchFilterCallback: (newFilter: CustomerSettingsOverviewSearchFilter) => void;
};

export const CustomerSettingsDataGrid = (props: CustomerSettingsDataGridProps) => {
  const { searchFilter, searchFilterCallback } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const navigate = useNavigate();
  const { scrollIntoView, scrollRef } = useScrollIntoView();

  const [rows, setRows] = useState<CustomerSettings[]>([]);
  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: searchFilter.pageSize,
    page: searchFilter.pageIndex,
  });

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
  }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
    scrollIntoView();
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
    }
    try {
      const result = await getAllCustomer(searchFilter);
      const customerResult = extendObjectWithId(result.data.results, 'member_id');
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);

        setTimeout(() => {
          setRows(customerResult);
        }, 100);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        updateData('loading', false);
      }
    }
  }, [isMounted, searchFilter]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  const onRowClick = (params) => {
    const id = params?.row?.id;
    if (!id) return;
    navigate(`${RoutesEnum.CustomerSettings}/${id}`);
  };

  const columns: GridColDef[] = [
    {
      field: 'member_id',
      headerName: t('customer:overviewPage.customerId'),
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.member_id || ''}</>;
      },
    },
    {
      field: 'lastName',
      headerName: t('customer:overviewPage.name'),
      flex: 2,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.lastName || ''}</>;
      },
    },
    {
      field: 'changedDate',
      headerName: t('customer:overviewPage.changedDate'),
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{DateHelper.getDateTime(params.row.changedDate) || ''}</>;
      },
    },
    {
      field: 'changedBy',
      headerName: t('customer:overviewPage.changedBy'),

      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.changedBy || ''}</>;
      },
    },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiPaper-root': {
          boxShadow: 'none',
        },
      }}
      ref={scrollRef}
    >
      <DataGrid<CustomerSettings>
        columns={columns}
        rows={rows}
        dataModel={data}
        checkboxSelection={false}
        onRowClick={onRowClick}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onSelectionModelChange={() => ConsoleHelper.log()}
        selectionModel={[]}
        sortModel={[
          {
            field: searchFilter.sortCol,
            sort: searchFilter.sortDir,
          },
        ]}
        onSortModelChange={(newSortModel) => {
          if (newSortModel.length > 0) {
            const { field } = newSortModel[0];
            searchFilterCallback({
              ...searchFilter,
              pageIndex: 0,
              sortCol: field,
              sortDir: newSortModel[0].sort,
            });
          } else {
            searchFilterCallback({
              ...searchFilter,
              pageIndex: 0,
              sortCol: searchFilter.sortCol,
              sortDir: searchFilter.sortDir === 'asc' ? 'desc' : 'asc',
            });
          }
        }}
      />
    </Box>
  );
};
